import * as React from "react";
import { BreadcrumbStyle } from "./breadcrumb-style";
import AppIcon from "common/app-icon";
import { getNamedIcon } from "common/named-icons";
import { ButtonVNext } from "designer/ui/smart/button-vnext/button-vnext";

interface Props<T> {
    readonly items: readonly T[];
    readonly currentItem: T;
    readonly nameForItem?: (item: T) => string;
    readonly onItemClicked?: (item: T) => void;

    readonly onItemRename?: (item: T, newName: string) => void;
}

interface RenameableBreadcrumbProps<T> {
    readonly currentItem: T;
    readonly nameForItem?: (item: T) => string;
    readonly onItemRename: (item: T, newName: string) => void;
}

function itemName<T>(item: T, nameForItem?: (item: T) => string): string {
    return nameForItem?.(item) ?? `${item}`;
}

function RenameableBreadcrumb<T>(props: React.PropsWithChildren<RenameableBreadcrumbProps<T>>): React.ReactElement {
    const { currentItem, nameForItem, onItemRename } = props;
    const [name, setName] = React.useState(itemName(currentItem, nameForItem));
    const [editing, setEditing] = React.useState(false);

    React.useEffect(() => {
        setName(itemName(currentItem, nameForItem));
    }, [currentItem, nameForItem]);

    const EditButton = getNamedIcon("editPencil2");

    if (editing) {
        return (
            <BreadcrumbStyle>
                <input
                    className="brcmb-current brcmb-item brcmb-editable"
                    type="text"
                    autoFocus={true}
                    value={name}
                    onChange={e => setName(e.target.value)}
                />

                <ButtonVNext
                    onClick={() => {
                        onItemRename(currentItem, name);
                        setEditing(false);
                    }}
                    className={"save-btn"}
                    buttonType={"primary"}
                    variant={"default"}
                    size={"md"}
                    label={"Save"}
                />
            </BreadcrumbStyle>
        );
    } else {
        return (
            <BreadcrumbStyle>
                <div className="brcmb-current brcmb-item brcmb-editable" onClick={() => setEditing(true)}>
                    {name}
                </div>
                <button className="edit-button" onClick={() => setEditing(true)}>
                    <EditButton />
                </button>
            </BreadcrumbStyle>
        );
    }
}

export default function Breadcrumb<T>(props: React.PropsWithChildren<Props<T>>): React.ReactElement {
    const { items, currentItem, nameForItem, onItemClicked, onItemRename } = props;

    // When there is only one item, we can support renaming
    if (items.length === 1 && onItemRename !== undefined) {
        return <RenameableBreadcrumb {...{ currentItem, onItemRename, nameForItem }} />;
    }

    return (
        <BreadcrumbStyle>
            {items.map((item, index) => {
                const isCurrent = item === currentItem;
                return (
                    <React.Fragment key={index}>
                        <button
                            onClick={onItemClicked !== undefined ? () => onItemClicked(item) : undefined}
                            className={isCurrent ? "brcmb-current brcmb-item" : "brcmb-item"}>
                            {itemName(item, nameForItem)}
                            {index < items.length - 1 && <AppIcon icon="caret" degreesOfRotation={270} size={16} />}
                        </button>
                    </React.Fragment>
                );
            })}
        </BreadcrumbStyle>
    );
}
